import * as React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { ArchivedOrderNotice } from '../../../shared/archiving/view/ArchivedOrderNotice';
import { ImagesBox } from './ImagesBox';
import { calculateCategoryCounts } from '../../imageCategoryChange/state/compute-category-counts';
import { IImageThumbnail } from '../../../Entities';
import { IExtraImageCost } from '../../listImages/queries/use-query-images';
import { ImagesSectionHeader } from './ImagesSectionHeader';
import { useCustomerImageProcessingData, ImageProcessingStatus } from '../state/use-customer-image-processing-data';
import { useApprovalWorkflow } from '../../approval/state/use-approval-workflow';
import { ApprovalSection } from '../../approval/view/ApprovalSection';
import { DeliverySection } from '../../delivery/view/DeliverySection';
import { CustomerImageProcessingPageSkeleton } from './CustomerImageProcessingPageSkeleton';

const ImageProcessingSection = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
});

type CustomerImageProcessingPageProps = {
    user: AuthenticatedUser;
    photoStatus: any;
    images: IImageThumbnail[];
    numberOfImages: number;
    productName: string;
    productProperties: any[];
    orderId: number;
    extraImageCost: IExtraImageCost | null;
    isApprovalInProgress: boolean;
    getEffectiveCategory: (image: IImageThumbnail) => number;
    toggleCategory: (imageId: number) => void;
};

type ExternalProps = {
    user: AuthenticatedUser;
    orderId: number;
};

function CustomerImageProcessingPage({
    user,
    photoStatus,
    images,
    numberOfImages,
    productName,
    productProperties,
    orderId,
    extraImageCost,
    isApprovalInProgress,
    getEffectiveCategory,
    toggleCategory
}: CustomerImageProcessingPageProps) {
    let { deliveredCount, nonStitchedDeliveredCount, orderedCount } = React.useMemo(
        () => calculateCategoryCounts(images, numberOfImages, getEffectiveCategory),
        [images, numberOfImages, getEffectiveCategory]
    );

    return (
        <ImageProcessingSection component="section">
            <ImagesSectionHeader imageCount={images.length} />
            <ImagesBox
                orderRowQuantitiesInfo={photoStatus.orderRowQuantitiesInfo}
                user={user}
                images={images}
                productName={productName}
                numberOfImages={numberOfImages}
                productProperties={productProperties}
                orderId={orderId}
                photoStatus={photoStatus.currentStatus}
                getEffectiveCategory={getEffectiveCategory}
                toggleCategory={toggleCategory}
                deliveredCount={deliveredCount}
                nonStitchedDeliveredCount={nonStitchedDeliveredCount}
                orderedCount={orderedCount}
                extraImageCost={extraImageCost}
                isApprovalInProgress={isApprovalInProgress}
            />
        </ImageProcessingSection>
    );
}

export default function DataLayer({ user, orderId }: ExternalProps) {
    let data = useCustomerImageProcessingData({ orderId, user });

    let currentStatus = data.status === ImageProcessingStatus.Ready ? data.photoStatus.currentStatus : 0;
    let approval = useApprovalWorkflow({ currentStatus, user });

    if (data.status === ImageProcessingStatus.Loading) {
        return <CustomerImageProcessingPageSkeleton />;
    }

    if (data.status === ImageProcessingStatus.Archived) {
        return <ArchivedOrderNotice onSubmit={data.onRetrieve} />;
    }

    if (data.status === ImageProcessingStatus.UnderProduction) {
        return (
            <section>
                <p>{Translate('image-processing/page/under-production')}</p>
            </section>
        );
    }

    let {
        photoStatus,
        uploadableProducts,
        allImages,
        getEffectiveCategory,
        toggleCategory,
        totalExtraImageCount,
        totalExtraImageCost,
        totalDeliveredCount
    } = data;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ApprovalSection
                orderId={orderId}
                user={user}
                currentStatus={photoStatus.currentStatus}
                allImages={allImages}
                totalExtraImageCount={totalExtraImageCount}
                totalExtraImageCost={totalExtraImageCost}
                totalDeliveredCount={totalDeliveredCount}
                getEffectiveCategory={getEffectiveCategory}
                onApprovalStarted={approval.onApprovalStarted}
                onApprovalCompleted={approval.onApprovalCompleted}
                onApprovalFailed={approval.onApprovalFailed}
            />
            <DeliverySection
                orderId={orderId}
                user={user}
                currentStatus={photoStatus.currentStatus}
                normalImagesAvailable={photoStatus.normalImagesAvailable}
                totalDeliveredCount={totalDeliveredCount}
                isApprovalInProgress={approval.isApprovalInProgress}
                approvalCompleted={approval.approvalCompleted}
            />
            {uploadableProducts.map((uploadableProductData) => (
                <CustomerImageProcessingPage
                    key={uploadableProductData.id}
                    user={user}
                    photoStatus={photoStatus}
                    images={uploadableProductData.thumbnails}
                    productName={uploadableProductData.name}
                    numberOfImages={uploadableProductData.numberOfImages}
                    productProperties={uploadableProductData.orderRowProductProperties}
                    orderId={orderId}
                    extraImageCost={uploadableProductData.extraImageCost}
                    isApprovalInProgress={approval.isApprovalInProgress}
                    getEffectiveCategory={getEffectiveCategory}
                    toggleCategory={toggleCategory}
                />
            ))}
        </Box>
    );
}
